import { teacherMessage } from './teacher-language.js';
import { DistrictLibrary } from './library.js';
import { Resources } from './resources.js';
/**
 * TIP Shell — Teacher Console (trusted chrome; parent DOM only).
 *
 * The teacher's driving seat for the §16.3 seam: see the classes you teach, connect
 * your own LMS account, attest a class↔LMS-course link, release REAL scores as DRAFT
 * grades, and BUILD a new app from a plain-language description (./builder.tsx).
 * PURE presentation: every decision (authz, destination, idempotency) is the SERVER's;
 * a deny renders as a message, never a retry-with-different-params.
 *
 * NOT HERE, deliberately: the tenant-wide audit ledger. It lives on the District
 * Console (./record.tsx, rendered by ./admin.tsx) as of 2026-07-25 — see that file's
 * header for why. What a teacher gets instead is the inline outcome sentence of the
 * action they just took, which says the same thing in their own register: "The link
 * is attested and on the record."
 *
 * Design (DESIGN.md, Paper & Seal): the console reads top-to-bottom like a
 * finely set document — identity, the connection, the class, the one action. The
 * serif appears only in on-the-record sentences; seal-red only on the actions
 * that put something on the record (attest, release, publish). Building and
 * releasing are separate screens of the main column, so each keeps exactly one
 * primary action.
 *
 * Nothing here is frame-reachable — the console renders only when NO sealed
 * frame is mounted, and none of this state crosses a Bridge. Data shown: class
 * names, assignment titles, provider course titles, counts — no structured
 * identity (names/emails/surrogate ids never appear as FIELDS; titles are
 * school-authored free text the same teacher already sees in the LMS — see
 * server/release/console.ts). Long surrogate uuids inside record sentences are
 * DISPLAY-truncated only (full sentence in the tooltip; the hash chain is the
 * authority, not this view).
 */
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { SealMark, AccountControls, SiteFooter } from './ui.js';
import { AddAppChoice, BuildSurface, type BuildPath } from './builder.js';
import { MyApps } from './my-apps.js';
import { SetupFlow, needsSetup, setupDismissed, dismissSetup, scopeSentence } from './setup.js';
import { ReconnectRequiredError } from './api.js';
import type {
  ServerClient, SessionInfo, OverviewResult, ClassOverview, AssignmentOverview,
  CoursesResult, ReleaseResult, RosterIntegrityReport, ConnectionStatus,
} from './api.js';

interface ConsoleProps {
  api: ServerClient;
  session: SessionInfo;
  /** Navigate to the student app view (?view=app) — a TOP-LEVEL nav, same page. */
  onOpenApp(): void;
}

type GoogleCourse = Extract<CoursesResult, { deny: false }>['courses'][number];
export function courseLabel(course: GoogleCourse): string {
  return course.section ? `${course.name} · ${course.section}` : course.name;
}
function uniqueCourses(courses: GoogleCourse[]): GoogleCourse[] {
  return [...new Map(courses.map(course => [course.externalClassId, course])).values()];
}

function GoogleCourseSection({ course, classes, courses, disabled, onConnect }: {
  course: GoogleCourse; classes: ClassOverview[]; courses: GoogleCourse[]; disabled: boolean;
  onConnect(classId: string, courseId: string): void;
}) {
  const [selected, setSelected] = useState('');
  const cls = classes.find(c => c.classId === selected);
  const oldCourse = courses.find(c => c.externalClassId === cls?.link?.externalClassId);
  return <details class="class-fold google-class-fold" onToggle={e => { if (!e.currentTarget.open) setSelected(''); }}>
    <summary><span class="class-name">{course.name}{course.section ? <span class="class-section">{course.section}</span> : null}</span>
      <span class="class-facts"><span>Google Classroom</span><span>Not connected in TIP</span></span></summary>
    <div class="class-body">
      <p class="class-guidance">If your school has already set up this class in TIP, connect it below. Otherwise ask your school’s TIP administrator to add its roster.</p>
      {classes.length > 0 ? <>
        <label class="class-connection-label" for={`tip-class-${course.externalClassId}`}>Already set up in TIP? Choose the same class</label>
        <select id={`tip-class-${course.externalClassId}`} class="field" value={cls ? selected : ''} disabled={disabled}
          onChange={e => setSelected(e.currentTarget.value)}>
          <option value="">Choose a TIP class</option>
          {classes.map(c => <option key={c.classId} value={c.classId}>{c.name}{c.link ? ' (already connected)' : ''}</option>)}
        </select>
        {cls?.link ? <p class="class-guidance">{cls.name} is currently connected to {oldCourse ? courseLabel(oldCourse) : 'another Google Classroom class'}. Review the change before saving.</p> : null}
        <div class="class-connection-actions"><button type="button" class="step" disabled={disabled || !cls}
          onClick={() => { if (cls) { onConnect(cls.classId, course.externalClassId); setSelected(''); } }}>Connect class</button></div>
      </> : <p class="class-guidance">Once your school has set up the roster, you can connect this Google Classroom class to it.</p>}
    </div>
  </details>;
}

export function TeacherConsole({ api, session, onOpenApp }: ConsoleProps) {
  const [overview, setOverview] = useState<OverviewResult | null>(null);
  const [courses, setCourses] = useState<CoursesResult | null>(null);
  const [connection, setConnection] = useState<ConnectionStatus | null>(null);
  const [coursesError, setCoursesError] = useState<string | null>(null);
  const [connectionError, setConnectionError] = useState(false);
  const [refreshing, setRefreshing] = useState(true);
  const refreshVersion = useRef(0);
  // Set when ANY Classroom-backed call answered reconnect_required. The connection card
  // trusts it over a "connected" status that was read a moment earlier: the route that
  // just talked to Google knows more than the status did.
  const [reconnectSeen, setReconnectSeen] = useState(false);
  const noteReconnect = (e: unknown): boolean => {
    if (!(e instanceof ReconnectRequiredError)) return false;
    setReconnectSeen(true);
    return true;
  };
  const [integrity, setIntegrity] = useState<RosterIntegrityReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // one action at a time
  const [results, setResults] = useState<Record<string, string>>({}); // per-action outcome sentence
  const [linkPick, setLinkPick] = useState<Record<string, string>>({}); // classId → chosen courseId
  const [pickerOpen, setPickerOpen] = useState<Record<string, boolean>>({});
  // Which task the main column is doing. Building an app and releasing grades are two
  // different jobs with two different primary actions, so they never share a screen.
  const params = typeof location === 'undefined' ? new URLSearchParams() : new URLSearchParams(location.search);
  const [mode, setMode] = useState<'classes' | 'build' | 'apps' | 'resources' | 'library'>(() => params.get('build') || params.get('workspace') === 'build' ? 'build' : params.get('workspace') === 'library' ? 'library' : params.get('workspace') === 'resources' ? 'resources' : params.get('workspace') === 'apps' ? 'apps' : 'classes');
  const [openBuildId, setOpenBuildId] = useState<string | undefined>(() => params.get('build') ?? undefined);
  const [newWorkspace, setNewWorkspace] = useState(0);
  const [builderBusy, setBuilderBusy] = useState(false);
  const remember = (next: 'classes' | 'build' | 'apps' | 'resources' | 'library', id?: string, nextPath = path, nextKind = newKind) => {
    if (typeof location === 'undefined') return;
    const u = new URL(location.href);
    u.searchParams.set('workspace', next);
    if (next === 'build' && !id && nextKind === 'resource') u.searchParams.set('kind', 'resource'); else u.searchParams.delete('kind');
    if (next === 'build' && !id) u.searchParams.set('path', nextPath); else u.searchParams.delete('path');
    if (next !== 'library') u.searchParams.delete('release');
    if (id) u.searchParams.set('build', id); else u.searchParams.delete('build');
    history.replaceState(null, '', u.toString());
  };
  const navigate = (next: 'classes' | 'build' | 'apps' | 'resources' | 'library', id?: string, nextPath = path, nextKind = newKind) => {
    if (next === 'build' && !id) { setNewWorkspace(n => n + 1); setNewKind(nextKind); }
    setOpenBuildId(id); setMode(next); remember(next, id, nextPath, nextKind);
  };
  const [newKind,setNewKind]=useState<'assignment'|'resource'>(()=>params.get('kind')==='resource'?'resource':'assignment');
  const [resourcesEnabled,setResourcesEnabled]=useState(false);
  useEffect(()=>{void Promise.resolve().then(()=>api.teacherBuildSettings()).then(r=>setResourcesEnabled(r.resourcesEnabled===true)).catch(()=>{});},[api]);
  const [path, setPath] = useState<BuildPath>(() => params.get('path') === 'describe' ? 'describe' : 'starter');
  // First-sign-in setup (./setup.tsx). Dismissed = done or skipped, remembered per browser.
  const [setupOff, setSetupOff] = useState<boolean>(() => setupDismissed());

  // NOTE: this deliberately does NOT read the audit ledger. The teacher console used
  // to fetch and render the tenant-wide record; it moved to the District Console on
  // 2026-07-25 (docs/CONSOLE-UX-BACKLOG.md item 4). A teacher's confirmation is the
  // inline outcome sentence of the action they just took — see `results` below — not
  // a chain of everyone else's events. GET /audit is unchanged and still teacher-
  // readable; this seat simply stopped asking for it.
  const refresh = useMemo(
    () => async () => {
      const version = ++refreshVersion.current;
      setRefreshing(true);
      const current = () => version === refreshVersion.current;
      // A slow provider or optional roster check must not hide already loaded TIP classes.
      const reads = [
        Promise.resolve().then(() => api.teacherOverview()).then(value => {
          if (current()) { setOverview(value); setError(null); }
        }).catch(() => { if (current()) setError('Your TIP classes could not be refreshed. Try again.'); }),
        Promise.resolve().then(() => api.teacherCourses()).then(value => {
          if (!current()) return;
          setCourses(value);
          setCoursesError(value.deny ? 'Google Classroom classes are unavailable. Check your connection and try again.' : null);
          if (!value.deny) setReconnectSeen(false);
        }).catch((reason: unknown) => {
          if (!current()) return;
          noteReconnect(reason);
          setCoursesError('Google Classroom classes could not be refreshed. Try again.');
        }),
        Promise.resolve().then(() => api.connectionStatus()).then(value => {
          if (current()) { setConnection(value); setConnectionError(false); }
        }).catch(() => { if (current()) setConnectionError(true); }),
      ];
      // This optional diagnostic never blocks class refresh or connection controls.
      void Promise.resolve().then(() => api.rosterIntegrity()).then(value => {
        if (current()) setIntegrity(value);
      }).catch(() => { if (current()) setIntegrity(null); });
      await Promise.all(reads);
      if (current()) setRefreshing(false);
    },
    [api],
  );
  useEffect(() => { void refresh(); return () => { refreshVersion.current++; }; }, [refresh]);
  useEffect(() => { document.title = 'Your classes · TIP'; }, []);

  // A finished connect attempt lands back here as ?connect=<code>, where the code comes
  // from a closed set the server owns. Read it ONCE and strip it from the URL, so a
  // refresh does not replay a stale outcome as if it just happened.
  const [connectOutcome, setConnectOutcome] = useState<string | null>(null);
  useEffect(() => {
    const code = new URLSearchParams(location.search).get('connect');
    if (!code) return;
    setConnectOutcome(code);
    const u = new URL(location.href);
    u.searchParams.delete('connect');
    history.replaceState(null, '', u.toString());
  }, []);

  const doDisconnect = async () => {
    setPickerOpen({}); setLinkPick({});
    setBusy('disconnect');
    try {
      const r = await api.disconnectProvider();
      setResults((m) => ({
        ...m,
        disconnect: !r.disconnected
          ? 'There was nothing connected to disconnect.'
          : r.revokedAtGoogle
            ? 'Disconnected. Google also confirmed that TIP no longer has access.'
            : 'Disconnected in TIP. To finish removing access, remove TIP from '
              + 'your Google account’s connections to other apps.',
      }));
    } catch (e) {
      setResults((m) => ({ ...m, disconnect: `⛔ ${teacherMessage(e, 'TIP could not confirm this action. Refresh the page to check its status before trying again.')}` }));
    }
    setBusy(null);
    void refresh();
  };

  const beginLink = (classId: string, courseId = '') => {
    setPickerOpen({ [classId]: true });
    setLinkPick({ [classId]: courseId });
    setResults((m) => ({ ...m, [`link:${classId}`]: '' }));
  };
  const cancelLink = () => { setPickerOpen({}); setLinkPick({}); };
  const doLink = async (classId: string) => {
    const courseId = linkPick[classId];
    const cls = overview && !overview.deny ? overview.classes.find(c => c.classId === classId) : undefined;
    const course = courses && !courses.deny ? courses.courses.find(c => c.externalClassId === courseId) : undefined;
    if (!courseId || !cls || !course || refreshing || coursesError || error || connectionError || busy !== null
      || reconnectSeen || connection?.needsReconnect || connection?.source !== 'own') return;
    setBusy(`link:${classId}`);
    try {
      const r = await api.linkCourse(classId, courseId);
      setResults((m) => ({
        ...m,
        [`link:${classId}`]: r.deny
          ? '⛔ This class connection could not be saved. Refresh classes, check your selection, and try again.'
          : `${cls.name} is connected to ${courseLabel(course)}. You choose when to send draft grades.`,
      }));
      if (!r.deny) cancelLink();
    } catch (e) {
      noteReconnect(e);
      setResults((m) => ({ ...m, [`link:${classId}`]: '⛔ We could not confirm the class connection. Refresh classes to check it before saving again.' }));
    }
    await refresh();
    setBusy(null);
  };

  const doRelease = async (a: AssignmentOverview) => {
    setBusy(`rel:${a.assignmentId}`);
    try {
      const r = await api.releaseGrades(a.assignmentId);
      if (!r.deny && r.failed.some((f) => f.reason === 'reconnect_required')) setReconnectSeen(true);
      setResults((m) => ({ ...m, [`rel:${a.assignmentId}`]: summarizeRelease(r) }));
    } catch (e) {
      noteReconnect(e);
      setResults((m) => ({ ...m, [`rel:${a.assignmentId}`]: `⛔ ${teacherMessage(e, 'TIP could not confirm this action. Refresh the page to check its status before trying again.')}` }));
    }
    setBusy(null);
    void refresh();
  };

  const classesNow = overview && !overview.deny ? overview.classes : null;
  // The school(s) this teacher's classes belong to (migration 0016) — the header line
  // Steve asked for instead of the teacher's own name. Two schools say so, not a guess.
  const schoolNames = [...new Set((classesNow ?? []).map((c) => c.school?.name).filter((n): n is string => !!n))];
  const schoolLine = schoolNames.length === 0 ? null
    : schoolNames.length === 1 ? schoolNames[0]! : `${schoolNames[0]} + ${schoolNames.length - 1} more`;
  const reconnect = reconnectSeen || connection?.needsReconnect === true;
  // Connected/returning teachers always reach the complete class directory.
  const showSetup = mode === 'classes' && connection !== null && classesNow !== null
    && needsSetup(connection, classesNow, courses, reconnect) && !setupOff;
  const courseList = courses && !courses.deny ? uniqueCourses(courses.courses) : [];
  const linkedCourseIds = new Set((classesNow ?? []).map(c => c.link?.externalClassId).filter(Boolean));
  const otherCourses = courseList.filter(c => !linkedCourseIds.has(c.externalClassId));
  const canConnect = connection?.source === 'own' && !reconnect && !connectionError && !coursesError && !error && !refreshing;
  const finishSetup = (p?: BuildPath) => {
    dismissSetup();
    setSetupOff(true);
    setConnectOutcome(null);
    if (p) { setPath(p); navigate('build', undefined, p, 'assignment'); }
  };

  return (
    <div>
      <header class="top">
        <SealMark />
        <span class="wordmark">TIP</span>
        <span class="crumb">For teachers</span>
        {schoolLine ? <span class="crumb school-crumb" title={schoolNames.join(', ')}>{schoolLine}</span> : null}
        <span class="spacer" />
        <button type="button" class="quiet" onClick={onOpenApp}>
          Open the student view →
        </button>
        <AccountControls api={api} role={session.role} />
      </header>
      <hr class="hairline top-rule" />

      <main class="sheet-field">
        <nav class="workspace-nav" aria-label="Teacher workspace">
          <button type="button" class="quiet" aria-current={mode === 'classes' ? 'page' : undefined}
            disabled={builderBusy} onClick={() => navigate('classes')}>Your classes</button>
          <button type="button" class="quiet" aria-current={mode === 'apps' || (mode === 'build' && !!params.get('build')) ? 'page' : undefined}
            disabled={builderBusy} onClick={() => navigate('apps')}>My apps</button>
          <button type="button" class="quiet" aria-current={mode === 'build' && !params.get('build') ? 'page' : undefined}
            disabled={builderBusy} onClick={() => { setPath('starter'); navigate('build', undefined, 'starter', 'assignment'); }}>Create</button>
          <button class="quiet" type="button" aria-current={mode === 'library' ? 'page' : undefined} disabled={builderBusy} onClick={() => navigate('library')}>Library</button>
          {resourcesEnabled ? <button class="quiet" aria-current={mode==='resources'?'page':undefined} disabled={builderBusy} onClick={()=>navigate('resources')}>Resources</button> : null}
        </nav>
        {error ? <div class="notice-line" role="alert">{error} <button type="button" class="step" disabled={refreshing || busy !== null} onClick={() => void refresh()}>Try loading TIP classes again</button></div> : null}

        {/* `solo`: one column. The second column was The Record; with it gone the
            teacher's work gets the full sheet — which is exactly the room the test-run
            frame and the source panel needed. */}
        <div class="doc solo">
          <div>
            {showSetup ? (
              <SetupFlow
                api={api}
                connection={connection!}
                classes={classesNow!}
                courses={courses}
                onFinish={finishSetup}
                onLinked={() => void refresh()}
              />
            ) : mode === 'library' ? (
              <DistrictLibrary api={api} userId={session.userId} classes={classesNow ?? []} onOpenBuild={id => navigate('build', id)}/>
            ) : mode === 'resources' ? (
              <Resources api={api} onNew={()=>{setPath('starter');navigate('build', undefined, 'starter', 'resource');}}/>
            ) : mode === 'apps' ? (
              <MyApps api={api} onOpen={(id) => navigate('build', id)} onNew={() => navigate('classes')} />
            ) : mode === 'build' ? (
              <BuildSurface
                key={openBuildId ?? `new-${newWorkspace}-${newKind}`}
                api={api}
                draftScope={session.tenantId && session.userId ? JSON.stringify([session.tenantId, session.userId]) : undefined}
                path={path}
                onPathChange={p => { setPath(p); const u = new URL(location.href); u.searchParams.set('path', p); history.replaceState(null, '', u.toString()); }}
                kind={newKind}
                initialBuildId={openBuildId}
                onRemember={(id) => remember('build', id)}
                onBusyChange={setBuilderBusy}
                onMyApps={() => navigate('apps')}
                // The classes this console ALREADY loaded — publishing needs one, and a
                // second fetch of the same truth could disagree with what is on screen.
                classes={overview && !overview.deny ? overview.classes : []}
                onLeave={() => navigate('classes')}
                onPublished={() => void refresh()}
              />
            ) : (
              <>
                <AddAppChoice onChoose={(p) => { setPath(p); navigate('build', undefined, p, 'assignment'); }} />

                {overview === null ? (
                  <p role="status">{error ? 'Your TIP classes are unavailable.' : 'Loading your classes…'}</p>
                ) : overview.deny ? (
                  <p class="notice-line">⛔ {teacherMessage(overview.reason, 'Your classes are not available for this account. Ask your school’s TIP administrator for help.')}</p>
                ) : (
                  <>
                    <ConnectionCard
                      status={connection}
                      loadingFailed={connectionError}
                      reconnect={reconnectSeen || connection?.needsReconnect === true}
                      startUrl={api.connectStartUrl()}
                      outcome={connectOutcome}
                      busy={busy}
                      result={results['disconnect']}
                      onDisconnect={() => void doDisconnect()}
                    />
                    <section class="class-directory" aria-label="Class directory">
                      <div class="class-directory-heading">
                        <div>
                          <h2 class="section-title classes-title">Classes in TIP</h2>
                          <p class="class-guidance">Open a class to manage its apps and Google Classroom connection.</p>
                        </div>
                        <button type="button" class="step" disabled={refreshing || busy !== null}
                          onClick={() => void refresh()}>{refreshing ? 'Refreshing classes…' : 'Refresh classes'}</button>
                      </div>
                      {connectionError ? <p class="notice-line" role="alert">We could not check your Google Classroom connection. Refresh classes to try again.</p> : null}
                      {refreshing ? <p class="class-guidance" role="status">Loading your latest class list…</p> : null}
                      {overview.classes.length === 0 ? <p class="class-empty">No classes are set up in TIP yet. Ask your school’s TIP administrator to set up your class roster. Your Google Classroom classes can still appear below.</p> : null}
                      {overview.classes.map((c) => (
                        <ClassSection key={c.classId} cls={c} only={!refreshing && overview.classes.length + otherCourses.length === 1}
                          courses={courses} busy={busy} results={results} linkPick={linkPick}
                          setLinkPick={setLinkPick} pickerOpen={pickerOpen} beginLink={beginLink}
                          cancelLink={cancelLink} canConnect={canConnect} doLink={doLink} doRelease={doRelease} />
                      ))}
                      <section class="google-class-directory" aria-labelledby="google-classes-title">
                        <h2 class="section-title classes-title" id="google-classes-title">Other Google Classroom classes</h2>
                        <p class="class-guidance">Active classes you teach with {connection?.account ? <strong>{connection.account}</strong> : 'your connected Google account'}. Connected classes appear in TIP above. Archived classes and classes you join as a student are not included.</p>
                        {connection?.source === 'own' && !reconnect ? <>
                          {coursesError ? <div class="class-load-error" role="alert">
                            <p>{coursesError} {courseList.length > 0 ? 'Showing the last loaded list. Connections are paused until it refreshes.' : ''}</p>
                            <button type="button" class="step" disabled={refreshing || busy !== null} onClick={() => void refresh()}>Try loading classes again</button>
                          </div> : null}
                          {courses === null && !coursesError ? <p role="status">Loading Google Classroom classes…</p> : null}
                          {!refreshing && !coursesError && courses && !courses.deny && otherCourses.length === 0 ? <p class="class-empty">{courseList.length === 0 ? 'No active teaching classes were found for this account.' : 'All Google Classroom classes in this list are already connected above.'}</p> : null}
                          {otherCourses.map(course => <GoogleCourseSection key={course.externalClassId} course={course}
                            classes={overview.classes} courses={courseList} disabled={!canConnect || busy !== null} onConnect={beginLink} />)}
                          <p class="class-guidance missing-class-help">Missing a class? Check that it is active and that the Google account shown above teaches it, then refresh classes.</p>
                        </> : <p class="class-empty">{reconnect ? 'Reconnect Google Classroom above to load your classes.' : 'Connect Google Classroom to see the active classes you teach. Your TIP classes remain available above.'}</p>}
                      </section>
                    </section>

                    <div class="section-gap">
                      <div class="ledger-line">
                        {integrity === null ? (
                          <span style="color:var(--ink-faint);">Class list check unavailable</span>
                        ) : integrity.ok ? (
                          <span><span class="dot ok" /><span class="state-ok">Class list checks passed</span>
                            <span style="color:var(--ink-faint);"> — student roles and grade levels match TIP’s rules</span></span>
                        ) : (
                          <span><span class="dot warn" /><span class="state-warn">
                            Class list needs attention</span>
                            <span style="color:var(--ink-soft);"> — {integrity.membersNotStudent.length +
                              integrity.classesTaughtByStudents.length +
                              integrity.studentsWithSuspectGrade.length} issue(s) for your TIP administrator to review</span></span>
                        )}
                      </div>
                      {integrity && integrity.studentsMissingGrade.length > 0 ? (
                        <div class="ledger-line" style="color:var(--ink-faint);font-size:13.5px;">
                          {integrity.studentsMissingGrade.length} student(s) have no grade level on file;
                          TIP does not share their names with apps. Ask your TIP administrator to add their grade levels.
                        </div>
                      ) : null}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </main>
      <SiteFooter api={api} />
    </div>
  );
}

/**
 * The LMS connection card — directly under “Add an app”, because nothing below
 * it can reach a gradebook without it.
 *
 * Five states, and the distinction between two of them is the point of the card:
 *   • CONNECTED (own)      — this teacher granted the permission; grades leave under
 *                            their own Google account.
 *   • SHARED CREDENTIAL    — grades still leave, but under the deployment's pilot
 *                            account, not theirs. Said out loud rather than shown as a
 *                            green "connected", because a teacher who does not know this
 *                            cannot make an informed choice about it.
 *   • NEEDS RECONNECTING   — Google stopped accepting TIP's access (the teacher removed
 *                            it, or the permission expired). Shown instead of "connected"
 *                            whenever the server says so OR any route just answered
 *                            reconnect_required; offers the same Connect button.
 *   • NOT CONNECTED        — the honest blocker, with the exact permissions the consent
 *                            screen will ask for listed BEFORE they press the button.
 *   • NOT CONNECTABLE      — the district has not configured an LMS client at all.
 */
function ConnectionCard(props: {
  status: ConnectionStatus | null;
  loadingFailed: boolean;
  /** Google refused the credential — overrides a "connected" source. */
  reconnect: boolean;
  startUrl: string;
  outcome: string | null;
  busy: string | null;
  result: string | undefined;
  onDisconnect(): void;
}) {
  const { status, outcome, busy } = props;

  const outcomeLine = outcome ? connectOutcomeSentence(outcome) : null;

  if (status === null) {
    if (props.loadingFailed) return null;
    return (
      <div class="ledger-line">
        <span style="color:var(--ink-faint);">Checking your Google Classroom connection…</span>
      </div>
    );
  }

  if (!status.connectable) {
    return (
      <div class="ledger-line">
        <span>
          <span class="dot warn" />
          <span class="state-warn">Google Classroom is not available yet</span>
          <span style="color:var(--ink-soft);">
            {' '}— your district has not set this up yet. You cannot connect classes or send grades yet.
            Everything else still works.
          </span>
        </span>
      </div>
    );
  }

  return (
    <div>
      {outcomeLine ? (
        <div class={`ledger-line ${outcome === 'ok' ? '' : 'notice-line'}`}>{outcomeLine}</div>
      ) : null}

      <div class="ledger-line">
        {props.reconnect ? (
          <span>
            <span class="dot warn" />
            <span class="state-warn">Google Classroom needs reconnecting</span>
            <span style="color:var(--ink-soft);">
              {' '}— Google stopped accepting TIP’s access to your Google Classroom
              {status.account ? <> (<code>{status.account}</code>)</> : null}. Reconnect to keep
              sending grades.
            </span>
          </span>
        ) : status.source === 'own' ? (
          <span>
            <span class="dot ok" />
            <span class="state-ok">Google Classroom connected</span>
            <span style="color:var(--ink-soft);">
              {' '}as <code>{status.account ?? 'your account'}</code>
              {status.connectedAt ? ` since ${formatDay(status.connectedAt)}` : ''}
            </span>
          </span>
        ) : status.source === 'pilot_bridge' ? (
          <span>
            <span class="dot warn" />
            <span class="state-warn">Using a shared district account</span>
            <span style="color:var(--ink-soft);">
              {' '}— grades are sent from someone else’s Google account. Connect your own account
              to send grades as yourself.
            </span>
          </span>
        ) : (
          <span>
            <span class="dot warn" />
            <span class="state-warn">Google Classroom not connected</span>
            <span style="color:var(--ink-soft);"> — grades have nowhere to go until you connect it.</span>
          </span>
        )}

        <span class="push">
          {status.source === 'own' && !props.reconnect ? (
            // `.step undo` — a real button, amber, never seal-red. Withdrawing an
            // authority is a deliberate act a teacher must be able to FIND, but it
            // adds no new fact about a student to the record.
            <button
              type="button"
              class="step undo"
              disabled={busy !== null}
              onClick={props.onDisconnect}
            >
              {busy === 'disconnect' ? 'Disconnecting…' : 'Disconnect'}
            </button>
          ) : (
            // A TOP-LEVEL navigation, deliberately an anchor: the consent screen is
            // Google's page and must own the whole tab, never a frame inside ours.
            <a class="act" href={props.startUrl} style="text-decoration:none;display:inline-block;">
              {props.reconnect ? 'Reconnect Google Classroom' : 'Connect Google Classroom'}
            </a>
          )}
        </span>
      </div>

      {props.result ? <div class="ledger-line" style="color:var(--ink-soft);">{props.result}</div> : null}

      {status.source !== 'own' || props.reconnect ? (
        <div class="ledger-line" style="color:var(--ink-faint);font-size:13.5px;">
          Google will ask you to allow TIP to: {status.scopes.map(scopeSentence).join('; ')}. Grades arrive
          as drafts (not yet shown to students). You still return them in Google Classroom.
        </div>
      ) : null}
    </div>
  );
}

/** The closed set of connect outcomes, as sentences a teacher can act on. */
function connectOutcomeSentence(code: string): string {
  switch (code) {
    case 'ok':
      return 'Your Google Classroom account is connected.';
    case 'declined':
      return 'You cancelled at Google, so nothing was connected.';
    case 'expired':
      return 'The connection request expired. Choose Connect Google Classroom to start again.';
    case 'mismatch':
      return 'That sign-in did not match the account you started from. Press Connect again from this page.';
    case 'partial_scopes':
      return 'TIP needs all the listed permissions to connect your classes and send grades. '
        + 'Choose Connect Google Classroom and select all the listed permissions.';
    case 'no_refresh_token':
      return 'Google did not allow TIP to stay connected. Remove TIP from your Google account’s '
        + 'connections to other apps, then choose Connect Google Classroom again.';
    case 'wrong_domain':
      return 'That Google account is not one your district has registered with TIP. Use your school account.';
    case 'unverified_email':
      return 'Google would not confirm that account’s email address, so TIP did not connect it.';
    default:
      return 'The connection could not be completed. Nothing was changed — try again in a moment.';
  }
}

/** A stored timestamp as a day a person would say out loud. */
function formatDay(iso: string): string {
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return iso;
  return new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * One class, as a row that opens.
 *
 * WHY COLLAPSED (Steve, 2026-09-30). Every class used to render open, one under another,
 * each with its link line and every assignment — so a teacher with five periods scrolled
 * past four classes to reach the fifth, and the class list read as one long form. Now the
 * closed row carries what a teacher scans for (students, whether it reaches Classroom, and
 * whether anything is waiting to be released), and opening it shows the rest. A native
 * <details>: keyboard and screen-reader behaviour for free, and no state to keep in sync.
 *
 * A single class opens by itself — there is nothing to choose between.
 */
function ClassSection(props: {
  cls: ClassOverview;
  /** The only class this teacher has ⇒ open it; a disclosure with one entry is a chore. */
  only: boolean;
  courses: CoursesResult | null;
  busy: string | null;
  results: Record<string, string>;
  linkPick: Record<string, string>;
  setLinkPick(updater: (m: Record<string, string>) => Record<string, string>): void;
  pickerOpen: Record<string, boolean>;
  beginLink(classId: string, courseId?: string): void;
  cancelLink(): void;
  canConnect: boolean;
  doLink(classId: string): Promise<void>;
  doRelease(a: AssignmentOverview): Promise<void>;
}) {
  const { cls, courses, busy, results } = props;
  const linkMsg = results[`link:${cls.classId}`];
  const courseList = courses && !courses.deny ? uniqueCourses(courses.courses) : [];
  const pickable = courseList.length > 0 && props.canConnect;
  const open = props.pickerOpen[cls.classId] ?? false;
  const selectRef = useRef<HTMLSelectElement>(null);
  const connectionButtonRef = useRef<HTMLButtonElement>(null);
  const [expanded, setExpanded] = useState(props.only);
  const wasEditing = useRef(false);
  const restoreFocus = useRef(false);
  const previousLink = useRef(cls.link?.externalClassId);
  const chosen = courseList.find(course => course.externalClassId === props.linkPick[cls.classId]);
  const courseName = (id: string): string => {
    const hit = courseList.find(course => course.externalClassId === id);
    return hit ? courseLabel(hit) : 'the previously connected class (not in the current active list)';
  };
  useEffect(() => { if (props.only) setExpanded(true); }, [props.only]);
  useEffect(() => {
    if (!open) return;
    setExpanded(true);
    // Run after the disclosure has opened, including when reached from a Google row.
    const timer = setTimeout(() => selectRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [open]);
  useEffect(() => {
    if (wasEditing.current && !open) restoreFocus.current = true;
    wasEditing.current = open;
    if (restoreFocus.current && !open && busy === null && pickable) {
      connectionButtonRef.current?.focus(); restoreFocus.current = false;
    }
  }, [open, busy, pickable]);
  useEffect(() => {
    if (open && previousLink.current !== cls.link?.externalClassId) props.cancelLink();
    previousLink.current = cls.link?.externalClassId;
  }, [cls.link?.externalClassId, open]);

  // What the closed row says: the facts a teacher scans a list of classes FOR.
  const ready = cls.assignments.filter((a) => a.realScores > 0 && a.posted < a.realScores).length;
  const apps = cls.assignments.length;

  return (
    <details class="class-fold" open={expanded} onToggle={e => setExpanded(e.currentTarget.open)}>
      <summary>
        <span class="class-name">{cls.name}{cls.link && courseList.find(course => course.externalClassId === cls.link?.externalClassId)?.section ? <span class="class-section">{courseList.find(course => course.externalClassId === cls.link?.externalClassId)!.section}</span> : null}</span>
        <span class="class-facts">
          <span>{cls.studentCount} student{cls.studentCount === 1 ? '' : 's'}</span>
          <span>{apps === 0 ? 'no apps yet' : `${apps} app${apps === 1 ? '' : 's'}`}</span>
          {cls.link ? (
            <span><span class="dot ok" />Connected to Google Classroom</span>
          ) : (
            <span><span class="dot warn" /><span class="state-warn">Not connected to Google Classroom</span></span>
          )}
          {ready > 0 ? (
            <span class="class-ready">{ready} ready to send</span>
          ) : null}
        </span>
      </summary>

      <div class="class-body">
      <div class="ledger-line">
        {cls.link ? (
          <span>
            <span class="dot ok" />
            <span class="state-ok">Connected</span>
            <span style="color:var(--ink-soft);"> to {courseName(cls.link.externalClassId)}</span>
          </span>
        ) : (
          <span>
            <span class="dot warn" />
            <span class="state-warn">Not connected</span>
            <span style="color:var(--ink-soft);"> Draft grades stay in TIP until you connect this class.</span>
          </span>
        )}
        {pickable ? (
          <span class="push">
            {!open ? (
              // The attestation itself is the seal-red act (below); opening the picker
              // is the step that gets you there — and on an unlinked class it is the
              // thing standing between the teacher and a working gradebook.
              <button
                ref={connectionButtonRef}
                type="button"
                class="step"
                disabled={busy !== null}
                onClick={() => props.beginLink(cls.classId)}
              >
                {cls.link ? 'Change class connection' : 'Connect class'}
              </button>
            ) : null}
          </span>
        ) : null}
      </div>

      {open ? (
        <div class="class-connection-editor">
          <h3>{cls.link ? 'Change connection for' : 'Connect'} {cls.name}</h3>
          <label class="class-connection-label" for={`google-course-${cls.classId}`}>Google Classroom class for {cls.name}</label>
          <select id={`google-course-${cls.classId}`} ref={selectRef} class="field"
            value={chosen?.externalClassId ?? ''} disabled={busy !== null || !pickable}
            onChange={e => { const value = e.currentTarget.value; props.setLinkPick(m => ({ ...m, [cls.classId]: value })); }}>
            <option value="">Choose the matching Google Classroom class</option>
            {courseList.map(course => <option key={course.externalClassId} value={course.externalClassId}>{courseLabel(course)}</option>)}
          </select>
          {chosen ? <div class="class-connection-confirmation" aria-live="polite">
            <p><strong>{cls.name}</strong> in TIP will connect to <strong>{courseLabel(chosen)}</strong> in Google Classroom.</p>
            {cls.link ? <p>Current destination: <strong>{courseName(cls.link.externalClassId)}</strong>.<br />New destination: <strong>{courseLabel(chosen)}</strong>.</p> : null}
            <p>Future draft grades you send from this TIP class will go to {courseLabel(chosen)}. Saving does not move students or send grades.</p>
          </div> : <p class="class-guidance">Choose the class with the same students. Saving this connection does not set up or change the roster.</p>}
          {!props.canConnect ? <p class="class-guidance" role="status">Refresh classes or reconnect Google Classroom before saving.</p> : null}
          <div class="class-connection-actions">
            <button type="button" class="act" disabled={busy !== null || !pickable || !chosen || chosen.externalClassId === cls.link?.externalClassId}
              onClick={() => void props.doLink(cls.classId)}>{busy === `link:${cls.classId}` ? 'Saving connection…' : 'Save class connection'}</button>
            <button type="button" class="quiet" disabled={busy !== null} onClick={props.cancelLink}>Cancel</button>
          </div>
        </div>
      ) : null}
      {linkMsg ? <div role="status" class={`action-result settle${linkMsg.startsWith('⛔') ? ' err' : ''}`}>{linkMsg}</div> : null}

      {cls.assignments.length === 0 ? (
        <div class="ledger-line" style="color:var(--ink-faint);">No assignments yet.</div>
      ) : (
        cls.assignments.map((a) => (
          <div class="asg-row" key={a.assignmentId}>
            <div class="row-top">
              <span class="title">{a.title}</span>
              <span class="sub">{a.appName} · out of {a.maxScore}</span>
            </div>
            <div class="row-act">
              <span class="ready">
                {a.realScores === 0
                  ? 'No scores yet'
                  : `${a.realScores} score${a.realScores === 1 ? '' : 's'} ready`}
              </span>
              {a.posted > 0 ? (
                <span><span class="dot ok" /><span class="state-ok">{a.posted} posted as drafts</span></span>
              ) : null}
              {a.failed > 0 ? (
                <span><span class="dot warn" /><span class="state-warn">{a.failed} failed</span></span>
              ) : null}
              {a.pending > 0 ? (
                <span style="color:var(--ink-soft);">{a.pending} pending</span>
              ) : null}
              <span class="push">
                {/* Seal-red only when it can actually go: releasing puts draft grades
                    in a real gradebook. When it cannot, it stays a visibly disabled
                    BUTTON (`.step`) rather than becoming faint text — "not yet" and
                    "not a control" are different things to say. */}
                <button
                  type="button"
                  class={cls.link && a.realScores > 0 ? 'act' : 'step'}
                  style={cls.link && a.realScores > 0 ? 'padding:10px 18px;' : ''}
                  disabled={busy !== null || !cls.link || a.realScores === 0}
                  title={!cls.link ? 'Connect this class to Google Classroom first' : a.realScores === 0 ? 'No student scores yet' : ''}
                  onClick={() => void props.doRelease(a)}
                >
                  {busy === `rel:${a.assignmentId}` ? 'Sending…' : 'Send grades to Google Classroom'}
                </button>
              </span>
            </div>
            {results[`rel:${a.assignmentId}`] ? (
              <div class={`action-result settle${results[`rel:${a.assignmentId}`]!.startsWith('⛔') ? ' err' : ''}`}>
                {results[`rel:${a.assignmentId}`]}
              </div>
            ) : null}
          </div>
        ))
      )}
      </div>
    </details>
  );
}

/** One-line, teacher-readable release outcome (mirrors the server's result fields). */
function summarizeRelease(r: ReleaseResult): string {
  if (r.deny) return `⛔ ${teacherMessage(r.reason, 'TIP did not send these grades. Check your class connection and try again. If it keeps happening, use Report an issue.')}`;
  const bits: string[] = [];
  if (r.posted.length) bits.push(`${r.posted.length} draft grade${r.posted.length === 1 ? '' : 's'} posted`);
  if (r.alreadyPosted.length) bits.push(`${r.alreadyPosted.length} already sent (not sent again)`);
  if (r.inFlight.length) bits.push(`${r.inFlight.length} already being sent`);
  if (r.skipped.length) bits.push(`${r.skipped.length} skipped (student has never signed in)`);
  if (r.failed.length) bits.push(`${r.failed.length} could not be confirmed (check Google Classroom before trying again)`);
  if (bits.length === 0) bits.push('No grades to send');
  const reconnect = r.failed.some((f) => f.reason === 'reconnect_required')
    ? ' Google disconnected TIP while grades were being sent. Reconnect Google Classroom, then send again. '
      + 'grades already sent will not be sent twice.'
    : '';
  return `${bits.join(' · ')}. Grades arrive as drafts. Return them in Google Classroom when you are ready for students to see them.${reconnect}`;
}
